import React, { useRef, useEffect, useState, useCallback } from 'react';
import { X, Play, MessageCircle } from 'lucide-react';

const TOTAL_NODES = 320;

// Demo participant data
const DEMO_COMMITMENTS = [
  'Innovation', 'Excellence', 'Growth', 'Leadership', 'Integrity', 
  'Passion', 'Courage', 'Teamwork', 'Trust', 'Impact',
  'Purpose', 'Care', 'Quality', 'Respect', 'Agility'
];

const DEMO_NAMES = [
  'Alex Chen', 'Sarah Miller', 'James Wilson', 'Emma Davis', 'Michael Brown',
  'Sophia Garcia', 'Liam Johnson', 'Olivia Martinez', 'Noah Anderson', 'Ava Thomas',
  'William Taylor', 'Isabella Moore', 'Benjamin Lee', 'Mia Jackson', 'Lucas White',
  'Charlotte Harris', 'Henry Clark', 'Amelia Lewis', 'Alexander Robinson', 'Harper Walker',
  'Daniel Hall', 'Evelyn Allen', 'Matthew Young', 'Abigail King', 'David Wright',
  'Emily Scott', 'Joseph Green', 'Elizabeth Baker', 'Samuel Adams', 'Sofia Nelson'
];

const DEMO_TITLES = [
  'Marketing Director', 'Product Manager', 'Sales Lead', 'Brand Strategist', 'Operations Manager',
  'Creative Director', 'HR Business Partner', 'Finance Analyst', 'Supply Chain Lead', 'R&D Scientist'
];

const DEMO_MESSAGES = [
  "Together, we're building something extraordinary.",
  "P&G taught me that real impact comes from putting consumers first.",
  "Innovation isn't just a buzzword here. We live it every single day.",
  "The energy in this room is incredible.",
  "Proud to be part of a team that exceeds expectations.",
  "Our strength lies in our diversity.",
  "When we work together, there's nothing we can't accomplish.",
  "This moment represents years of hard work and dedication."
];

const getAvatarUrl = (seed) => {
  return `https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}&backgroundColor=transparent`;
};

const generateParticipant = (index) => {
  const seed = `pg-contributor-${index}-${index * 7 + 13}`;
  return {
    id: `p${index}`,
    name: DEMO_NAMES[index % DEMO_NAMES.length],
    title: DEMO_TITLES[index % DEMO_TITLES.length],
    commitment: DEMO_COMMITMENTS[index % DEMO_COMMITMENTS.length],
    message: DEMO_MESSAGES[index % DEMO_MESSAGES.length],
    hasVideo: index % 5 === 0,
    avatarUrl: getAvatarUrl(seed),
    seed
  };
};

// Participant Panel Component
const ParticipantPanel = ({ participant, onClose }) => {
  if (!participant) return null;

  return (
    <div 
      className="fixed right-0 top-0 h-full w-full max-w-md bg-slate-900/95 backdrop-blur-xl border-l border-slate-700/50 z-50 overflow-y-auto"
      data-testid="participant-panel"
    >
      <button 
        onClick={onClose}
        className="absolute top-6 right-6 w-10 h-10 rounded-full bg-slate-800/80 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
        data-testid="close-panel-btn"
      >
        <X size={20} />
      </button>

      <div className="p-8 pt-20">
        <div className="relative w-32 h-32 mx-auto mb-6">
          <div 
            className="w-full h-full rounded-full overflow-hidden"
            style={{ 
              background: 'linear-gradient(135deg, #1e3a5f 0%, #0d1b2a 100%)',
              boxShadow: '0 0 40px rgba(79, 195, 247, 0.4)'
            }}
          >
            <img 
              src={participant.avatarUrl} 
              alt={participant.name}
              className="w-full h-full object-cover"
              style={{ filter: 'hue-rotate(180deg) saturate(0.5) brightness(1.2)' }}
            />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white text-center font-['Outfit'] mb-1">
          {participant.name}
        </h2>
        <p className="text-slate-400 text-center mb-6">{participant.title}</p>

        <div className="flex justify-center mb-8">
          <div className="px-6 py-3 rounded-full bg-blue-500/20 border border-blue-500/30">
            <span className="text-blue-400 font-semibold text-lg">"{participant.commitment}"</span>
          </div>
        </div>

        <div className="bg-slate-800/50 rounded-2xl p-6 mb-6">
          {participant.hasVideo ? (
            <div className="relative">
              <div className="aspect-video bg-slate-900 rounded-xl flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-blue-500/30 flex items-center justify-center cursor-pointer hover:bg-blue-500/50 transition-all">
                  <Play size={32} className="text-white ml-1" />
                </div>
              </div>
              <p className="text-slate-500 text-sm text-center mt-3">Click to play message</p>
            </div>
          ) : (
            <div className="flex gap-4">
              <MessageCircle size={24} className="text-blue-400 flex-shrink-0" />
              <p className="text-slate-300 italic">"{participant.message}"</p>
            </div>
          )}
        </div>

        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-slate-500 text-sm">
            <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
            Contributing energy to P&G
          </div>
        </div>
      </div>
    </div>
  );
};

const EnergyTree = ({ currentCount, teslaTriggered, superGlowActive, onTeslaComplete }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const pulsesRef = useRef([]);
  const teslaStartTimeRef = useRef(null);
  const arcsRef = useRef([]);
  const superGlowArcsRef = useRef([]);
  const centerRippleRef = useRef(0);
  const centerImpactsRef = useRef([]); // For stacking impact effects
  const nodesRef = useRef([]);
  const hoveredNodeRef = useRef(null);
  const staticNodesRef = useRef(null);
  const avatarImagesRef = useRef({});
  const nodeEmissionStateRef = useRef({}); // Track per-node emission state

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Seeded random number generator
  const createSeededRandom = useCallback((seed) => {
    let state = seed;
    return () => {
      state = (state * 1103515245 + 12345) & 0x7fffffff;
      return state / 0x7fffffff;
    };
  }, []);

  // Poisson-disc sampling for organic distribution
  const poissonDiscSampling = useCallback((width, height, minDist, centerX, centerY, exclusionRadius, maxPoints, seed) => {
    const random = createSeededRandom(seed);
    const cellSize = minDist / Math.sqrt(2);
    const gridWidth = Math.ceil(width / cellSize);
    const gridHeight = Math.ceil(height / cellSize);
    const grid = new Array(gridWidth * gridHeight).fill(null);
    const points = [];
    const active = [];

    const gridIndex = (x, y) => {
      const gx = Math.floor(x / cellSize);
      const gy = Math.floor(y / cellSize);
      if (gx < 0 || gx >= gridWidth || gy < 0 || gy >= gridHeight) return -1;
      return gy * gridWidth + gx;
    };

    const isValidPoint = (x, y) => {
      if (x < minDist || x > width - minDist || y < minDist || y > height - minDist) return false;
      
      const dx = (x - centerX) / (width * 0.48);
      const dy = (y - centerY) / (height * 0.44);
      const distFromCenter = Math.sqrt(dx * dx + dy * dy) * Math.max(width * 0.48, height * 0.44);
      if (distFromCenter < exclusionRadius) return false;

      const gx = Math.floor(x / cellSize);
      const gy = Math.floor(y / cellSize);
      
      for (let i = Math.max(0, gx - 2); i <= Math.min(gridWidth - 1, gx + 2); i++) {
        for (let j = Math.max(0, gy - 2); j <= Math.min(gridHeight - 1, gy + 2); j++) {
          const idx = j * gridWidth + i;
          if (grid[idx] !== null) {
            const px = grid[idx].x;
            const py = grid[idx].y;
            const d = Math.sqrt((x - px) ** 2 + (y - py) ** 2);
            if (d < minDist) return false;
          }
        }
      }
      return true;
    };

    const seedPoints = [
      { x: width * 0.2, y: height * 0.3 },
      { x: width * 0.8, y: height * 0.3 },
      { x: width * 0.2, y: height * 0.7 },
      { x: width * 0.8, y: height * 0.7 },
      { x: width * 0.5, y: height * 0.15 },
      { x: width * 0.5, y: height * 0.85 }
    ];

    for (const sp of seedPoints) {
      if (isValidPoint(sp.x, sp.y) && points.length < maxPoints) {
        const idx = gridIndex(sp.x, sp.y);
        if (idx >= 0) {
          grid[idx] = { x: sp.x, y: sp.y };
          points.push({ x: sp.x, y: sp.y });
          active.push(points.length - 1);
        }
      }
    }

    const k = 30;

    while (active.length > 0 && points.length < maxPoints) {
      const activeIdx = Math.floor(random() * active.length);
      const point = points[active[activeIdx]];
      let found = false;

      for (let attempt = 0; attempt < k; attempt++) {
        const angle = random() * Math.PI * 2;
        const radius = minDist + random() * minDist;
        const newX = point.x + Math.cos(angle) * radius;
        const newY = point.y + Math.sin(angle) * radius;

        if (isValidPoint(newX, newY)) {
          const idx = gridIndex(newX, newY);
          if (idx >= 0 && grid[idx] === null) {
            grid[idx] = { x: newX, y: newY };
            points.push({ x: newX, y: newY });
            active.push(points.length - 1);
            found = true;
            break;
          }
        }
      }

      if (!found) {
        active.splice(activeIdx, 1);
      }
    }

    return points;
  }, [createSeededRandom]);

  // Generate ORGANIC positions
  const generateOrganicNodePositions = useCallback((width, height, activeCount) => {
    if (activeCount === 0) return [];
    
    const centerX = width / 2;
    const centerY = height / 2;
    const exclusionRadius = 190;
    
    const baseDist = 70;
    const minDist = Math.max(45, baseDist - (activeCount / 10));
    
    const points = poissonDiscSampling(
      width, height, minDist, 
      centerX, centerY, exclusionRadius,
      activeCount + 50,
      12345
    );

    const random = createSeededRandom(54321);
    const shuffled = [...points].sort(() => random() - 0.5);
    const selected = shuffled.slice(0, activeCount);

    const nodes = selected.map((point, index) => {
      const dx = point.x - centerX;
      const dy = point.y - centerY;
      const distFromCenter = Math.sqrt(dx * dx + dy * dy);
      const maxDist = Math.sqrt((width/2) ** 2 + (height/2) ** 2);
      const distRatio = distFromCenter / maxDist;
      
      const size = 32 + (1 - distRatio) * 12;
      
      // Generate unique emission timing for each node
      const nodeRandom = createSeededRandom(index * 997 + 12345);
      const emissionInterval = 600 + nodeRandom() * 1200; // 0.6s to 1.8s
      const startDelay = nodeRandom() * 2000; // Random start offset
      const maxPulsesInFlight = 2 + Math.floor(nodeRandom() * 3); // 2-4 pulses per node
      
      return {
        x: point.x,
        y: point.y,
        index,
        size,
        depthFactor: 1 - distRatio,
        participant: generateParticipant(index),
        distFromCenter,
        emissionInterval,
        startDelay,
        maxPulsesInFlight
      };
    });

    return nodes;
  }, [poissonDiscSampling, createSeededRandom]);

  // Preload avatar images
  useEffect(() => {
    if (staticNodesRef.current) {
      staticNodesRef.current.forEach(node => {
        if (!avatarImagesRef.current[node.participant.seed]) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = node.participant.avatarUrl;
          img.onload = () => {
            avatarImagesRef.current[node.participant.seed] = img;
          };
        }
      });
    }
  }, [staticNodesRef.current]);

  // Update static nodes and reset emission state when count changes
  useEffect(() => {
    if (dimensions.width > 0 && dimensions.height > 0) {
      staticNodesRef.current = generateOrganicNodePositions(dimensions.width, dimensions.height, currentCount);
      nodesRef.current = staticNodesRef.current;
      
      // Initialize emission state for each node
      const newEmissionState = {};
      staticNodesRef.current.forEach(node => {
        newEmissionState[node.index] = {
          lastEmitTime: 0,
          pulsesInFlight: 0,
          initialized: false
        };
      });
      nodeEmissionStateRef.current = newEmissionState;
    }
  }, [dimensions, currentCount, generateOrganicNodePositions]);

  useEffect(() => {
    const handleResize = () => {
      setDimensions({ width: window.innerWidth, height: window.innerHeight });
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mouse interaction
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      let found = null;
      const nodes = nodesRef.current || [];
      for (let i = nodes.length - 1; i >= 0; i--) {
        const node = nodes[i];
        const dx = x - node.x;
        const dy = y - node.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist < node.size / 2 + 6) {
          found = node;
          break;
        }
      }

      hoveredNodeRef.current = found;
      setHoveredIndex(found ? found.index : null);
      canvas.style.cursor = found ? 'pointer' : 'default';
    };

    const handleClick = () => {
      if (hoveredNodeRef.current) {
        setSelectedParticipant(hoveredNodeRef.current.participant);
      }
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('click', handleClick);
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('click', handleClick);
    };
  }, []);

  // Create energy pulse with varied speed
  const createPulse = useCallback((node, centerX, centerY) => {
    const dx = centerX - node.x;
    const dy = centerY - node.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    // Vary curve direction and strength per pulse
    const curveStrength = 20 + Math.random() * 25;
    const perpX = -dy / dist * curveStrength;
    const perpY = dx / dist * curveStrength;
    const curveDir = Math.random() > 0.5 ? 1 : -1;
    
    // Varied speed per pulse (0.002 to 0.005)
    const speed = 0.002 + Math.random() * 0.003;
    
    return {
      startX: node.x,
      startY: node.y,
      controlX: (node.x + centerX) / 2 + perpX * curveDir,
      controlY: (node.y + centerY) / 2 + perpY * curveDir,
      endX: centerX,
      endY: centerY,
      progress: 0,
      speed,
      size: 3 + Math.random() * 2,
      trailLength: 12 + Math.floor(Math.random() * 8),
      nodeIndex: node.index
    };
  }, []);

  // Quadratic bezier point
  const getQuadraticPoint = (t, x0, y0, x1, y1, x2, y2) => {
    const mt = 1 - t;
    return {
      x: mt * mt * x0 + 2 * mt * t * x1 + t * t * x2,
      y: mt * mt * y0 + 2 * mt * t * y1 + t * t * y2
    };
  };

  // Draw avatar with blue tint
  const drawAvatar = useCallback((ctx, node, isHovered, superGlow) => {
    const { x, y, size, participant } = node;
    const displaySize = isHovered ? size * 1.1 : size;
    const glowBoost = superGlow ? 0.15 : 0;
    
    const glowIntensity = isHovered ? 0.5 : 0.25 + glowBoost;
    const glowSize = displaySize + (isHovered ? 14 : 10);
    const gradient = ctx.createRadialGradient(x, y, displaySize * 0.3, x, y, glowSize);
    gradient.addColorStop(0, `rgba(79, 195, 247, ${glowIntensity})`);
    gradient.addColorStop(0.5, `rgba(33, 150, 243, ${glowIntensity * 0.4})`);
    gradient.addColorStop(1, 'rgba(33, 150, 243, 0)');
    ctx.beginPath();
    ctx.arc(x, y, glowSize, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();

    ctx.beginPath();
    ctx.arc(x, y, displaySize / 2, 0, Math.PI * 2);
    const bgGradient = ctx.createRadialGradient(
      x - displaySize * 0.1, y - displaySize * 0.1, 0,
      x, y, displaySize / 2
    );
    bgGradient.addColorStop(0, 'rgba(30, 58, 95, 1)');
    bgGradient.addColorStop(1, 'rgba(13, 27, 42, 1)');
    ctx.fillStyle = bgGradient;
    ctx.fill();

    const avatarImg = avatarImagesRef.current[participant.seed];
    if (avatarImg && avatarImg.complete) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, displaySize / 2 - 2, 0, Math.PI * 2);
      ctx.clip();
      
      const imgSize = displaySize - 4;
      ctx.drawImage(avatarImg, x - imgSize / 2, y - imgSize / 2, imgSize, imgSize);
      
      ctx.fillStyle = 'rgba(30, 60, 100, 0.4)';
      ctx.fillRect(x - imgSize / 2, y - imgSize / 2, imgSize, imgSize);
      
      ctx.restore();
    } else {
      const silhouetteColor = 'rgba(79, 195, 247, 0.65)';
      ctx.beginPath();
      ctx.arc(x, y - displaySize * 0.08, displaySize * 0.18, 0, Math.PI * 2);
      ctx.fillStyle = silhouetteColor;
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(x, y + displaySize * 0.22, displaySize * 0.28, displaySize * 0.15, 0, Math.PI, 0);
      ctx.fillStyle = silhouetteColor;
      ctx.fill();
    }

    ctx.beginPath();
    ctx.arc(x, y, displaySize / 2, 0, Math.PI * 2);
    ctx.strokeStyle = isHovered ? 'rgba(79, 195, 247, 0.9)' : 'rgba(79, 195, 247, 0.4)';
    ctx.lineWidth = isHovered ? 2.5 : 1.5;
    ctx.stroke();
  }, []);

  // Main animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || dimensions.width === 0) return;

    const ctx = canvas.getContext('2d');
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    const centerX = dimensions.width / 2;
    const centerY = dimensions.height / 2;
    let startTime = null;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const nodes = staticNodesRef.current || [];
      const energy = currentCount / TOTAL_NODES;

      // Tesla effect — auto-repeats every 3 seconds
      let teslaIntensity = 0;
      const TESLA_CYCLE = 3000; // 3s cycle
      const TESLA_ACTIVE = 2200; // effect duration within cycle
      const cyclePos = elapsed % TESLA_CYCLE; // position within current cycle
      const cycleT = cyclePos / 1000; // in seconds

      if (cyclePos < TESLA_ACTIVE) {
        // Ramp up 0-0.3s, hold 0.3-1.6s, fade 1.6-2.2s
        if (cycleT < 0.3) {
          teslaIntensity = cycleT / 0.3;
        } else if (cycleT < 1.6) {
          teslaIntensity = 1;
        } else {
          teslaIntensity = Math.max(0, 1 - (cycleT - 1.6) / 0.6);
        }

        // Spawn arcs during the peak phase
        if (cycleT >= 0.4 && cycleT < 1.4 && arcsRef.current.length < 8) {
          for (let i = 0; i < 3; i++) {
            const angle = Math.random() * Math.PI * 2;
            const radius = 100 + Math.random() * 180;
            arcsRef.current.push({
              startX: centerX, startY: centerY,
              endX: centerX + Math.cos(angle) * radius,
              endY: centerY + Math.sin(angle) * radius,
              life: 1
            });
          }
        }

        // Flash at the tail end
        if (cycleT >= 1.6) {
          const flashAlpha = Math.max(0, 1 - (cycleT - 1.6) / 0.6);
          ctx.fillStyle = `rgba(79, 195, 247, ${flashAlpha * 0.15})`;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      }

      // Super Glow arcs
      if (superGlowActive) {
        if (superGlowArcsRef.current.length < 3 && Math.random() < 0.02) {
          const angle = Math.random() * Math.PI * 2;
          const radius = 85 + Math.random() * 50;
          superGlowArcsRef.current.push({
            startX: centerX, startY: centerY,
            endX: centerX + Math.cos(angle) * radius,
            endY: centerY + Math.sin(angle) * radius,
            life: 1
          });
        }
      }

      // Draw subtle curved guide paths (reduced opacity)
      nodes.forEach(node => {
        const dx = centerX - node.x;
        const dy = centerY - node.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const curveStrength = 22;
        const perpX = -dy / dist * curveStrength;
        const perpY = dx / dist * curveStrength;
        const curveDir = (node.index % 2 === 0) ? 1 : -1;
        
        ctx.beginPath();
        ctx.moveTo(node.x, node.y);
        ctx.quadraticCurveTo(
          (node.x + centerX) / 2 + perpX * curveDir,
          (node.y + centerY) / 2 + perpY * curveDir,
          centerX, centerY
        );
        ctx.strokeStyle = `rgba(79, 195, 247, ${0.015 + energy * 0.01})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      });

      // Count pulses per node for tracking
      const pulsesPerNode = {};
      pulsesRef.current.forEach(pulse => {
        pulsesPerNode[pulse.nodeIndex] = (pulsesPerNode[pulse.nodeIndex] || 0) + 1;
      });

      // Update and draw energy pulses with trails
      let pulsesArrivingCount = 0;
      const globalSpeedMod = 1 + energy * 0.3 + teslaIntensity * 0.3 + (superGlowActive ? 0.15 : 0);
      
      pulsesRef.current = pulsesRef.current.filter(pulse => {
        pulse.progress += pulse.speed * globalSpeedMod;
        if (pulse.progress >= 1) {
          pulsesArrivingCount++;
          // Decrement pulse count for node
          const state = nodeEmissionStateRef.current[pulse.nodeIndex];
          if (state) state.pulsesInFlight = Math.max(0, state.pulsesInFlight - 1);
          
          // Create impact effect
          centerImpactsRef.current.push({
            radius: 0,
            maxRadius: 25 + Math.random() * 15,
            opacity: 0.6,
            speed: 2
          });
          
          return false;
        }

        const head = getQuadraticPoint(
          pulse.progress,
          pulse.startX, pulse.startY,
          pulse.controlX, pulse.controlY,
          pulse.endX, pulse.endY
        );

        // Draw trailing streak (slightly reduced opacity for density)
        const trailSteps = pulse.trailLength;
        for (let i = trailSteps; i >= 0; i--) {
          const trailProgress = Math.max(0, pulse.progress - i * 0.008);
          const tPos = getQuadraticPoint(
            trailProgress,
            pulse.startX, pulse.startY,
            pulse.controlX, pulse.controlY,
            pulse.endX, pulse.endY
          );
          
          const fadeRatio = 1 - (i / trailSteps);
          const trailAlpha = fadeRatio * (1 - pulse.progress * 0.25) * 0.5; // Reduced for density
          const trailSize = pulse.size * (0.25 + fadeRatio * 0.75);
          
          ctx.beginPath();
          ctx.arc(tPos.x, tPos.y, trailSize, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(79, 195, 247, ${trailAlpha})`;
          ctx.fill();
        }

        // Bright pulse head
        const headGlow = ctx.createRadialGradient(head.x, head.y, 0, head.x, head.y, pulse.size * 1.8);
        headGlow.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
        headGlow.addColorStop(0.3, 'rgba(79, 195, 247, 0.6)');
        headGlow.addColorStop(1, 'rgba(79, 195, 247, 0)');
        ctx.beginPath();
        ctx.arc(head.x, head.y, pulse.size * 1.8, 0, Math.PI * 2);
        ctx.fillStyle = headGlow;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(head.x, head.y, pulse.size * 0.5, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 1)';
        ctx.fill();

        return true;
      });

      // Update center impact effects (stacking ripples)
      centerImpactsRef.current = centerImpactsRef.current.filter(impact => {
        impact.radius += impact.speed;
        impact.opacity -= 0.03;
        
        if (impact.opacity <= 0) return false;
        
        ctx.beginPath();
        ctx.arc(centerX, centerY, impact.radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(200, 230, 255, ${impact.opacity})`;
        ctx.lineWidth = 2;
        ctx.stroke();
        
        return true;
      });

      // Update center ripple based on arrivals
      centerRippleRef.current = Math.min(1, centerRippleRef.current + pulsesArrivingCount * 0.08);
      centerRippleRef.current *= 0.92;

      // Draw STATIC nodes
      const sortedNodes = [...nodes].sort((a, b) => a.depthFactor - b.depthFactor);
      
      sortedNodes.forEach(node => {
        const isHovered = hoveredIndex === node.index;
        drawAvatar(ctx, node, isHovered, superGlowActive);

        // ASYNCHRONOUS PULSE EMISSION - each node has own timing
        const state = nodeEmissionStateRef.current[node.index];
        if (state) {
          // Apply start delay on first emission
          if (!state.initialized) {
            state.lastEmitTime = elapsed - node.emissionInterval + node.startDelay;
            state.initialized = true;
          }
          
          const timeSinceLastEmit = elapsed - state.lastEmitTime;
          
          // Check if ready to emit AND has room for more pulses
          if (timeSinceLastEmit >= node.emissionInterval && 
              state.pulsesInFlight < node.maxPulsesInFlight) {
            state.lastEmitTime = elapsed;
            state.pulsesInFlight++;
            pulsesRef.current.push(createPulse(node, centerX, centerY));
          }
        }
      });

      // Draw Tesla arcs
      arcsRef.current = arcsRef.current.filter(arc => {
        arc.life -= 0.04;
        if (arc.life <= 0) return false;
        
        ctx.beginPath();
        ctx.moveTo(arc.startX, arc.startY);
        for (let i = 0; i < 5; i++) {
          const t = (i + 1) / 5;
          ctx.lineTo(
            arc.startX + (arc.endX - arc.startX) * t + (Math.random() - 0.5) * 35,
            arc.startY + (arc.endY - arc.startY) * t + (Math.random() - 0.5) * 35
          );
        }
        ctx.strokeStyle = `rgba(255, 255, 255, ${arc.life})`;
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.strokeStyle = `rgba(79, 195, 247, ${arc.life * 0.5})`;
        ctx.lineWidth = 5;
        ctx.stroke();
        return true;
      });

      // Draw Super Glow arcs
      superGlowArcsRef.current = superGlowArcsRef.current.filter(arc => {
        arc.life -= 0.02;
        if (arc.life <= 0) return false;
        
        ctx.beginPath();
        ctx.moveTo(arc.startX, arc.startY);
        for (let i = 0; i < 4; i++) {
          const t = (i + 1) / 4;
          ctx.lineTo(
            arc.startX + (arc.endX - arc.startX) * t + (Math.random() - 0.5) * 18,
            arc.startY + (arc.endY - arc.startY) * t + (Math.random() - 0.5) * 18
          );
        }
        ctx.strokeStyle = `rgba(200, 230, 255, ${arc.life * 0.5})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
        return true;
      });

      // Draw center P&G core with enhanced impact response
      const baseRadius = 80;
      const superGlowBoost = superGlowActive ? 0.6 : 0;
      const rippleBoost = centerRippleRef.current * 15;
      const coreRadius = baseRadius + energy * 18 + teslaIntensity * 22 + superGlowBoost * 28 + rippleBoost;
      const dynamicGlow = energy * 0.35 + teslaIntensity * 0.35 + centerRippleRef.current * 0.5 + superGlowBoost;

      const atmosphereRadius = superGlowActive ? coreRadius * 4 : coreRadius * 3;
      const outerGrad = ctx.createRadialGradient(centerX, centerY, coreRadius * 0.5, centerX, centerY, atmosphereRadius);
      outerGrad.addColorStop(0, `rgba(79, 195, 247, ${dynamicGlow * 0.3})`);
      outerGrad.addColorStop(0.3, `rgba(33, 150, 243, ${dynamicGlow * 0.12})`);
      outerGrad.addColorStop(1, 'rgba(33, 150, 243, 0)');
      ctx.beginPath();
      ctx.arc(centerX, centerY, atmosphereRadius, 0, Math.PI * 2);
      ctx.fillStyle = outerGrad;
      ctx.fill();

      if (superGlowActive) {
        const midGrad = ctx.createRadialGradient(centerX, centerY, coreRadius * 0.3, centerX, centerY, coreRadius * 1.8);
        midGrad.addColorStop(0, 'rgba(200, 230, 255, 0.4)');
        midGrad.addColorStop(0.5, 'rgba(79, 195, 247, 0.2)');
        midGrad.addColorStop(1, 'rgba(33, 150, 243, 0)');
        ctx.beginPath();
        ctx.arc(centerX, centerY, coreRadius * 1.8, 0, Math.PI * 2);
        ctx.fillStyle = midGrad;
        ctx.fill();
      }

      const coreGrad = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, coreRadius);
      if (superGlowActive) {
        coreGrad.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
        coreGrad.addColorStop(0.3, 'rgba(200, 230, 255, 0.8)');
        coreGrad.addColorStop(0.6, 'rgba(79, 195, 247, 0.5)');
        coreGrad.addColorStop(1, 'rgba(33, 150, 243, 0.25)');
      } else {
        coreGrad.addColorStop(0, `rgba(200, 230, 255, ${0.55 + dynamicGlow * 0.35})`);
        coreGrad.addColorStop(0.5, `rgba(79, 195, 247, ${0.3 + dynamicGlow * 0.25})`);
        coreGrad.addColorStop(1, `rgba(33, 150, 243, ${0.12 + dynamicGlow * 0.15})`);
      }
      ctx.beginPath();
      ctx.arc(centerX, centerY, coreRadius, 0, Math.PI * 2);
      ctx.fillStyle = coreGrad;
      ctx.fill();
      
      ctx.strokeStyle = superGlowActive 
        ? 'rgba(200, 230, 255, 0.8)' 
        : `rgba(79, 195, 247, ${0.45 + dynamicGlow * 0.35})`;
      ctx.lineWidth = superGlowActive ? 3 : 2 + dynamicGlow * 2;
      ctx.stroke();

      const fontSize = 48 + energy * 4 + centerRippleRef.current * 5 + (superGlowActive ? 8 : 0);
      ctx.font = `700 ${fontSize}px Outfit, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = superGlowActive ? 'rgba(200, 230, 255, 1)' : 'rgba(79, 195, 247, 0.85)';
      ctx.shadowBlur = superGlowActive ? 50 : 20 + dynamicGlow * 30;
      ctx.fillStyle = superGlowActive ? 'rgba(255, 255, 255, 1)' : `rgba(255, 255, 255, ${0.8 + dynamicGlow * 0.15})`;
      ctx.fillText('P&G', centerX, centerY);
      ctx.shadowBlur = 0;

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [dimensions, currentCount, teslaTriggered, superGlowActive, hoveredIndex, createPulse, drawAvatar, onTeslaComplete]);

  useEffect(() => {
    if (!teslaTriggered) {
      teslaStartTimeRef.current = null;
      arcsRef.current = [];
    }
  }, [teslaTriggered]);

  useEffect(() => {
    if (!superGlowActive) {
      superGlowArcsRef.current = [];
    }
  }, [superGlowActive]);

  return (
    <>
      <canvas
        ref={canvasRef}
        data-testid="energy-tree-canvas"
        className="canvas-container"
        style={{ display: 'block' }}
      />
      <ParticipantPanel 
        participant={selectedParticipant} 
        onClose={() => setSelectedParticipant(null)} 
      />
    </>
  );
};

export default EnergyTree;
